/**
 * Reusable right-click context menu (Preact port).
 *
 * Renders a single positionable popup attached to the dialog root. Closes on
 * outside click, another contextmenu, Escape, blur, or scroll.
 */
import { useEffect, useState } from 'preact/hooks';

export function showContextMenu(items, x, y) {
  // Dispatch a custom event carrying the menu request; App listens and renders.
  window.dispatchEvent(
    new CustomEvent('talos:contextmenu', { detail: { items, x, y } })
  );
}

export function hideContextMenu() {
  window.dispatchEvent(new CustomEvent('talos:contextmenu:hide'));
}

/**
 * ContextMenuHost renders the currently open context menu (if any) and
 * subscribes to open/hide custom events. Mount once at the app root.
 */
export function ContextMenuHost() {
  const [menu, setMenu] = useState(null);

  useEffect(() => {
    const onOpen = (e) => setMenu(e.detail);
    const onHide = () => setMenu(null);
    window.addEventListener('talos:contextmenu', onOpen);
    window.addEventListener('talos:contextmenu:hide', onHide);
    return () => {
      window.removeEventListener('talos:contextmenu', onOpen);
      window.removeEventListener('talos:contextmenu:hide', onHide);
    };
  }, []);

  if (!menu) return null;
  return <ContextMenu items={menu.items} x={menu.x} y={menu.y} onClose={hideContextMenu} />;
}

function ContextMenu({ items, x, y, onClose }) {
  const [pos, setPos] = useState({ left: x, top: y });

  useEffect(() => {
    // Clamp to the viewport on mount.
    const el = document.getElementById('context-menu-root');
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pad = 8;
    let left = x;
    let top = y;
    if (left + rect.width > window.innerWidth - pad) left = window.innerWidth - rect.width - pad;
    if (top + rect.height > window.innerHeight - pad) top = window.innerHeight - rect.height - pad;
    left = Math.max(pad, left);
    top = Math.max(pad, top);
    setPos({ left, top });
  }, [x, y]);

  useEffect(() => {
    const onDown = (e) => {
      const el = document.getElementById('context-menu-root');
      if (el && !el.contains(e.target)) onClose();
    };
    const onContext = (e) => {
      const el = document.getElementById('context-menu-root');
      if (el && !el.contains(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    const onScroll = () => onClose();
    const onBlur = () => onClose();
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('contextmenu', onContext, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('blur', onBlur);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('contextmenu', onContext, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [onClose]);

  return (
    <div
      id="context-menu-root"
      className="fixed z-50 rounded-lg border border-base-300 bg-base-100 shadow-lg py-1 text-sm min-w-[160px]"
      style={{ left: pos.left, top: pos.top }}
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-base-content hover:bg-base-200 transition-colors"
          onClick={() => {
            onClose();
            if (typeof item.onClick === 'function') item.onClick();
          }}
        >
          {item.label ?? item.id}
        </button>
      ))}
    </div>
  );
}